/**
 * The one back button.
 *
 * A round white disc with a chevron, lifted off the page by a soft shadow. It
 * started life on A1 ("who's here today?") and every other way back — a text
 * pill here, a brown circle there, a bare `‹ Back` somewhere else — was a
 * different answer to the same question.
 *
 * 🔴 IT IS ICON-ONLY, SO `label` IS REQUIRED. The chevron carries no text, so
 * the accessible name has to come from `aria-label`; making it a required prop
 * is what stops the next screen from shipping an unnamed button. Pass the
 * translated string (`t.back`), never a hard-coded "Back".
 *
 * Pass `href` for a link (the hash routes) or `onClick` for a handler (the kid
 * flow, which is state-machine driven and has no URL to go to). `href` wins if
 * both are given, because a real link is navigable and a button is not.
 */

/** 44×44 — the minimum comfortable touch target, and it is a child's thumb. */
const BASE =
  'flex h-[44px] w-[44px] shrink-0 items-center justify-center rounded-full bg-white text-[#332D27] shadow-[0_4px_12px_rgba(0,0,0,0.08)] transition-transform duration-150 hover:scale-105'

export interface BackButtonProps {
  /** Renders an `<a>`. A hash route, e.g. `#/` or `#/start`. */
  href?: string
  /** Renders a `<button>` when there is no `href`. */
  onClick?: () => void
  /** The accessible name, already translated. */
  label: string
  /** Positioning only — the disc's own styling is not meant to be overridden. */
  className?: string
  /**
   * True while the child's body is pointing at this button.
   *
   * 🔴 BACK IS AN OPTION IN THE SCAN, AND IT IS INDEX 0 BECAUSE THAT IS WHERE IT
   * SITS. `useBodyChoice` moves a highlight left and right, so the leftmost
   * thing on screen has to be the leftmost thing in the scan — a child reaching
   * left past the first card arrives here, which is where their arm is already
   * pointing. Putting it last would make *"go back"* mean *"reach right"*.
   *
   * ⚠️ DRAWN, NOT DECIDED, HERE. The screen owns the scan; this renders a ring
   * and a fill.
   */
  bodyHovered?: boolean
  /** 0 → 1 while a hand is held up over this button. */
  bodyProgress?: number
}

function Chevron() {
  return (
    <svg
      className="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth="2.5"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
    </svg>
  )
}

export function BackButton({
  href,
  onClick,
  label,
  className,
  bodyHovered = false,
  bodyProgress = 0,
}: BackButtonProps) {
  const base = className ? `${BASE} ${className}` : BASE
  /*
   * The ring goes on the disc itself rather than on a wrapper, because this is
   * positioned absolutely by most of its callers — a wrapping element would need
   * their `className` to keep the position, and then two things would own it.
   */
  const cls = bodyHovered ? `${base} ring-[6px] ring-primary-bright` : base

  /*
   * 🔴 THE HOLD, VISIBLE HERE TOO. A back button that took 700 ms with nothing
   * moving is the same failure LL-1 describes for a dwell on a stone: a child
   * concludes it did not work and stops holding at 600 ms, every time.
   */
  const fill =
    bodyHovered && bodyProgress > 0 ? (
      <span className="pointer-events-none absolute inset-x-1 bottom-1 h-1.5 overflow-hidden rounded-pill bg-line/50">
        <span
          className="block h-full bg-primary-bright transition-[width] duration-75"
          style={{ width: `${String(Math.round(bodyProgress * 100))}%` }}
        />
      </span>
    ) : null

  if (href) {
    return (
      <a href={href} data-nav="back" aria-label={label} className={cls}>
        <Chevron />
        {fill}
      </a>
    )
  }

  return (
    <button type="button" onClick={onClick} data-nav="back" aria-label={label} className={cls}>
      <Chevron />
      {fill}
    </button>
  )
}
